import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';

import GroundTruthEnableQuestion from './GroundTruthEnableQuestion';

import {
  sectionAnswerShape, translatableShape, scoresShape, aggregationShape,
} from '../../types';
import GTTable from './GTTable';

const guidRegex = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

export default class GroundTruthMultipleChoice extends Component {
  specificQuestionRegex = new RegExp(`[0-9]+.[0-9]+.(${this.props.question.uuid}).${guidRegex}`);

  unityRegex = new RegExp('unity.[0-9]+.[0-9]+.[0-9]+');

  componentDidUpdate(prevProps) {
    // set max score based on new scoreCard update
    const { scores: prevScore } = prevProps;
    if (!prevScore) {
      return;
    }
    const { scores, sectionId, questionId } = this.props;
    const prevMaxScore = prevScore[`maximumScorePerQuestion.${sectionId}.${questionId}`];
    const maxScore = this.getMaximumScore(scores);
    if (prevMaxScore !== maxScore) {
      this.onChangeMinMaxScore('maximumScorePerQuestion', maxScore, sectionId, questionId);
    }
  }

  onChangeMinMaxScore = (name, value, sectionId, questionId) => {
    const fullKey = `${name}.${sectionId}.${questionId}`;
    const { setScore } = this.props;
    setScore(fullKey, value);
  };

  /**
   * Gets the maximum score only for the messy states object
   * The score are stored state props in format like
   * [sectionIndex].[sampleIndex].[questionUUID].[optionUUID];
   * The special regex above check for this property
   */
  getMaximumScore = () => {
    const { scores } = this.props;
    let score = 0;
    const interestedKeys = Object.keys(scores)
      .filter((key) => this.specificQuestionRegex.test(key));
    for (let i = 0; i < interestedKeys.length; i += 1) {
      score += scores[interestedKeys[i]];
    }
    return score;
  };

  renderScoreSelect = (aggregation, sampleIndex, sectionIndex, questionUuid, optionUuid) => {
    const uuid = `${questionUuid}.${optionUuid}`;
    const scoreKey = `${sectionIndex}.${sampleIndex}.${uuid}`;
    const scoreValue = this.props.scores[scoreKey] || 0;

    return (
      <Box display="flex" flexDirection="row" justifyContent="center">
        <Box>
          <Select
            variant="standard"
            id={
              `GroundTruthScore-${sectionIndex}-${sampleIndex}-${uuid}`
            }
            value={scoreValue}
            onChange={(event) => {
              const score = event.target.value;
              this.props.setScore(scoreKey, score);
            }}
            disabled={this.props.disabled}
          >
            <MenuItem value={0}>0</MenuItem>
            <MenuItem value={1}>1</MenuItem>
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={3}>3</MenuItem>
          </Select>
        </Box>
        <Box>
          (
          {
            aggregation.getOccurrences(
              sampleIndex,
              sectionIndex,
              uuid,
            ) || 0
          }
          )
        </Box>
      </Box>
    );
  };

  renderControlUnityCheck = (caseIndex) => {
    const {
      scores,
      sectionId,
      questionId,
      setScore,
    } = this.props;
    const currKey = `unity.${sectionId}.${questionId}.${caseIndex}`;
    const currChecked = scores[currKey];
    return (
      <div>
        <FormControlLabel
          control={(
            <Tooltip
              title={<FormattedMessage id="groundtruthmultiplechoice.unitymessage" />}
              placement="top"
            >
              <Checkbox
                checked={!!currChecked}
                color="primary"
                onChange={(e) => {
                  const key = `unity.${sectionId}.${questionId}.${caseIndex}`;
                  setScore(key, e.target.checked);
                }}
              />
            </Tooltip>
          )}
          label="Unity score"
          labelPlacement="start"
        />
      </div>
    );
  };

  render() {
    const {
      sectionId, questionId, disabled, currentUnity,
    } = this.props;
    return (
      <div
        style={{
          margin: 5,
          marginBottom: 40,
        }}
      >
        <hr />
        <GTTable
          question={this.props.question}
          answers={this.props.answers}
          aggregation={this.props.aggregation}
          section={this.props.section}
          sectionId={this.props.sectionId}
          numSamples={this.props.numSamples}
          renderScoreSelect={this.renderScoreSelect}
          type="MultipleChoice"
          additionalActionPerCase={currentUnity ? this.renderControlUnityCheck : undefined}
        />
        <GroundTruthEnableQuestion
          onChange={this.onChangeMinMaxScore}
          currentMinimumScore={this.props.currentMinimumScore}
          currentMaximumScore={this.props.currentMaximumScore}
          hideMinimumScore={this.props.hideMinimumScore}
          disabled={disabled}
          disableMaximumScore
          sectionId={sectionId}
          questionId={questionId}
        />
      </div>
    );
  }
}

GroundTruthMultipleChoice.defaultProps = {
  sectionId: undefined,
  setScore: () => undefined,
  scores: undefined,
  aggregation: undefined,
  currentMinimumScore: undefined,
  currentMaximumScore: undefined,
  hideMinimumScore: true,
  disabled: false,
  currentUnity: undefined,
};

GroundTruthMultipleChoice.propTypes = {
  numSamples: PropTypes.number.isRequired,
  question: translatableShape.isRequired,
  setScore: PropTypes.func,
  scores: scoresShape,
  answers: sectionAnswerShape.isRequired,
  sectionId: PropTypes.number,
  aggregation: aggregationShape,
  section: PropTypes.shape({ caseNames: PropTypes.arrayOf(PropTypes.string) }).isRequired,
  questionId: PropTypes.number.isRequired,
  currentMinimumScore: PropTypes.number,
  currentMaximumScore: PropTypes.number,
  hideMinimumScore: PropTypes.bool,
  disabled: PropTypes.bool,
  currentUnity: PropTypes.arrayOf(PropTypes.bool),
};
